import { Link, Head } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell from '../../components/layout/TeacherShell';
import { Card } from '../../components/ui';
import { FrequencyBadge, TierBadge, PosBadge } from '../../components/dictionary';
import { cn } from '../../lib/cn';

export default function VocabularyListsShow({ list }) {
  const { t } = useTranslation();
  return (
    <TeacherShell scrollTitle={<><Link href="/teacher/vocabulary_lists" className="hover:text-blue-600 dark:hover:text-blue-400">{t('nav.vocabulary')}</Link>{'  ›  '}{list.name}</>}>
      <Head title={`${list.name} - Vocabulary Lists`} />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Link */}
        <Link
          href="/teacher/vocabulary_lists"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-6"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('vocabLists.backToLists')}
        </Link>

        {/* Header */}
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {list.name}
          </h1>
          {list.description && (
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {list.description}
            </p>
          )}
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <span>{list.wordCount} {t('common.words')}</span>
            {list.source && (
              <>
                <span>•</span>
                <span>{t('vocabLists.source')} {list.source}</span>
              </>
            )}
          </div>
        </header>

        {/* Words List */}
        <div className="space-y-2">
          {list.words.map((word, idx) => (
            <Link key={word.id} href={`/teacher/dictionary/${word.id}`}>
              <Card className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-gray-400 dark:text-gray-500 font-mono text-sm w-8">
                      {idx + 1}.
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {word.lemma}
                        </span>
                        <PosBadge pos={word.pos} size="sm" />
                      </div>
                      {word.primaryDefinition && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-1">
                          {word.primaryDefinition}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FrequencyBadge band={word.frequencyBand} size="sm" />
                    <TierBadge tier={word.beckTier} size="sm" />
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>

        {list.words.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {t('vocabLists.emptyList')}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-center gap-4 mt-8">
          <Link
            href="/teacher/vocabulary_lists"
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('vocabLists.viewAllLists')}
          </Link>
          <Link
            href="/teacher/dictionary"
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('vocabLists.browseDictionary')}
          </Link>
        </div>
      </div>
    </TeacherShell>
  );
}
